// useNotificationData.js - FIXED
import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { GET_NOTIFICATIONS } from "adapters/notification.service";
import { getAllWatchedTasks, getWatchedTaskState, startWatchingTask } from "redux/redux-thunk/BackgroundtaskThunks";
import { useDispatch } from "react-redux";
import { calculatePriority, isTaskInProgress } from "./NotificationHelpers";

const FETCH_THROTTLE_MS = 1000;
const TASK_POLL_INTERVAL = 1000;

export const useNotificationData = (isModalOpen) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [serverNotifications, setServerNotifications] = useState([]);
    const [activeTaskStates, setActiveTaskStates] = useState([]);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isFetching, setIsFetching] = useState(false);

    const lastFetchTimeRef = useRef(0);
    const isFetchingRef = useRef(false);
    const isUpdatingRef = useRef(false);

    const localNotifications = useSelector((state) => state.app?.notification || []);

    // Keep refs in sync with state
    useEffect(() => {
        isFetchingRef.current = isFetching;
    }, [isFetching]);

    useEffect(() => {
        isUpdatingRef.current = isUpdating;
    }, [isUpdating]);

    // Poll active task states with deep equality check
    useEffect(() => {
        const interval = setInterval(() => {
            const tasks = getAllWatchedTasks();
            const taskArray = tasks || [];

            // Only update if actually changed (deep equality check on key fields)
            setActiveTaskStates(prev => {
                if (prev.length !== taskArray.length) return taskArray;

                const hasChanges = taskArray.some((task, idx) => {
                    const prevTask = prev[idx];
                    return !prevTask ||
                        prevTask.id !== task.id ||
                        prevTask.status !== task.status ||
                        prevTask.progress !== task.progress ||
                        prevTask.message !== task.message;
                });

                return hasChanges ? taskArray : prev;
            });
        }, TASK_POLL_INTERVAL);

        return () => clearInterval(interval);
    }, []);

    // Fetch notifications from server - memoized with stable dependencies
    const loadNotifications = useCallback(async () => {
        const now = Date.now();

        // Use refs to avoid stale closures
        if (isUpdatingRef.current || isFetchingRef.current || now - lastFetchTimeRef.current < FETCH_THROTTLE_MS) {
            return;
        }

        setIsFetching(true);
        isFetchingRef.current = true;
        lastFetchTimeRef.current = now;

        try {
            const data = await GET_NOTIFICATIONS();
            const validNotifications = (data || []).filter(n => n?.addedOn && n?.taskId);

            setServerNotifications(validNotifications);

            // Start watching in-progress tasks
            validNotifications.forEach((notification) => {
                if (
                    notification.type === "background_task" &&
                    isTaskInProgress(notification.status)
                ) {
                    const taskId = notification.taskId || notification.id;
                    const category = notification.task_category || "generic";
                    const existingState = getWatchedTaskState(taskId);

                    if (!existingState) {
                        dispatch(startWatchingTask(taskId, category, () => { }));
                    }
                }
            });
        } catch (error) {
            console.error("Failed to fetch notifications:", error);
            toast.error(error);
        } finally {
            setIsFetching(false);
            isFetchingRef.current = false;
        }
    }, [dispatch, t]); // Removed isUpdating and isFetching from deps

    // Combine and merge all notifications
    const allNotifications = useMemo(() => {
        const localToasts = localNotifications.filter(n => n?.addedOn);
        const merged = [...serverNotifications];

        // Update with latest task states
        activeTaskStates.forEach((task) => {
            const existingIndex = merged.findIndex(
                n => n.taskId === task.id || n.id === task.id
            );

            if (existingIndex >= 0) {
                merged[existingIndex] = {
                    ...merged[existingIndex],
                    status: task.status,
                    progress: task.progress,
                    message: task.message,
                    title: task.title,
                    lastUpdated: task.lastUpdated,
                };
            } else if (isTaskInProgress(task.status)) {
                merged.push({
                    ...task,
                    type: "background_task",
                    addedOn: task.addedOn || Date.now(),
                    taskId: task.id,
                });
            }
        });

        // Combine with local notifications
        const combined = [...localToasts, ...merged];

        // Deduplicate efficiently
        const seen = new Map();
        const unique = combined.filter(notification => {
            const id = notification.basicResourceId ||
                notification.taskId ||
                notification.id ||
                `${notification.addedOn}_${notification.type}`;

            if (seen.has(id)) return false;
            seen.set(id, true);
            return true;
        });

        // Sort by priority then date
        unique.sort((a, b) => {
            const priorityDiff = calculatePriority(a) - calculatePriority(b);
            if (priorityDiff !== 0) return priorityDiff;
            return (b.addedOn || 0) - (a.addedOn || 0);
        });

        return unique;
    }, [localNotifications, serverNotifications, activeTaskStates]);

    return {
        allNotifications,
        serverNotifications,
        setServerNotifications,
        activeTaskStates,
        isUpdating,
        setIsUpdating,
        isFetching,
        loadNotifications,
    };
};